import { configureApiClient } from '@borradh-workspace/api-client';
import type { RuntimeConfig } from '@borradh-workspace/runtime-config/schema';

import { clearAdminTabToken } from '@/lib/admin-tab-token';
import { clearAuthToken } from '@/lib/auth-token';
import { handleBillingError } from '@/lib/billing-recovery';
import { clearImpersonationToken } from '@/lib/impersonation-token';
import { getRequestAuthToken, usesBearerAuth } from '@/lib/request-auth-token';
import {
  getSessionLoss,
  handleSessionInvalidation,
} from '@/lib/session-lifecycle';

let configured = false;
let apiBaseUrl: string | null = null;

/**
 * The apiUrl the shared client was configured with. Used by transports that
 * deliberately bypass the shared client (patient-portal auth — a second
 * principal whose bearer token must not collide with the staff
 * authProvider on the shared instance).
 */
export function getApiBaseUrl(): string | null {
  return apiBaseUrl;
}

export function configureApiClientForApp(config: RuntimeConfig): void {
  apiBaseUrl = config.apiUrl;
  if (configured) return;

  // Bearer-token auth is for NATIVE and LOCAL DEV only — never production web.
  //
  // `X-Client-Type: mobile` is what makes the API return a session token in the
  // sign-in body; the SPA then persists it via `secure-storage`, whose web
  // fallback is `localStorage`. Sending it unconditionally meant production web
  // kept a 7-day bearer in localStorage, readable by any XSS, which defeats the
  // HttpOnly cookie sitting next to it (ENG-577 H4).
  //
  // Native genuinely needs it (Keychain / EncryptedSharedPreferences, no cookie
  // jar). Local dev needs it because Vite on localhost:5173 calling a tunnelled
  // API is CROSS-SITE, so SameSite=Lax cookies are not sent and the session
  // reads back null. Production web is same-site (app.* and api.* share the
  // registrable domain), so the cookie works and the token is pure liability.
  const useBearerToken = usesBearerAuth();

  configureApiClient({
    baseUrl: config.apiUrl,
    // Tab-scoped identity first (impersonation, then the admin's own tab
    // token), then the native/dev bearer — see `request-auth-token.ts`, which
    // every hand-rolled request shares so none of them can drift from this.
    authProvider: async () => getRequestAuthToken(),
    headers: useBearerToken ? { 'X-Client-Type': 'mobile' } : {},
    onError: (error) => {
      handleBillingError(error);
      const loss = getSessionLoss(error);
      if (loss === 'expired') {
        clearAuthToken();
        clearAdminTabToken();
        clearImpersonationToken();
      }
      if (loss) {
        // An expired bearer — or, on web, a session cookie the browser has
        // already deleted — used to leave the user on the protected screen,
        // every action failing with the raw API message. Move through the
        // same explicit sign-out path instead of treating a normal session
        // lifecycle event as an app error.
        handleSessionInvalidation(loss);
      }
    },
  });
  configured = true;
}
